import { useEffect, useState } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';

import { Footer } from '../../../components/Footer';
import { Logo } from '../../../components/Logo';
import { MobileDrawer } from '../../../components/MobileDrawer';
import { useAdminAuth } from '../auth/useAdminAuth';

interface AdminNavItem {
  to: string;
  label: string;
  icon: JSX.Element;
}

const ADMIN_NAV: AdminNavItem[] = [
  {
    to: '/admin/dashboard',
    label: 'Dashboard',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
        <path d="M3 13h8V3H3v10zm10 8h8V11h-8v10zM3 21h8v-6H3v6zm10-18v6h8V3h-8z" />
      </svg>
    ),
  },
  {
    to: '/admin/tenants',
    label: 'Tenants',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    to: '/admin/plans',
    label: 'Planes',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M3 9h18M9 21V9" />
      </svg>
    ),
  },
  {
    to: '/admin/platform-providers',
    label: 'Providers',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
        <circle cx="12" cy="12" r="3" />
        <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
      </svg>
    ),
  },
  {
    to: '/admin/model-prices',
    label: 'Tarifas IA',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
        <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
  },
  {
    to: '/admin/di-resources',
    label: 'Recursos DI',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
      </svg>
    ),
  },
  {
    to: '/admin/ocr-providers',
    label: 'Proveedores OCR',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
        <path d="M4 7V5a1 1 0 0 1 1-1h2M17 4h2a1 1 0 0 1 1 1v2M20 17v2a1 1 0 0 1-1 1h-2M7 20H5a1 1 0 0 1-1-1v-2" />
        <rect x="8" y="8" width="8" height="8" rx="1" />
      </svg>
    ),
  },
  {
    to: '/admin/prompts',
    label: 'Prompts',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
        <path d="M4 4h16v12H5l-1 4V4z" />
        <path d="M8 9h8M8 13h5" />
      </svg>
    ),
  },
  {
    to: '/admin/email/rules',
    label: 'Alertas email',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m3 7 9 6 9-6" />
      </svg>
    ),
  },
  {
    to: '/admin/preparation/effective',
    label: 'Política prep',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
        <path d="M4 6h16M4 12h10M4 18h7" />
        <path d="M17 14l4 4-4 4" />
      </svg>
    ),
  },
  {
    to: '/admin/preparation/cu-governance',
    label: 'Coste CU',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    ),
  },
];

/** Admin nav list + logout — shared by the desktop aside and the mobile drawer. */
function AdminNav({ onLogout }: { onLogout: () => void }): JSX.Element {
  return (
    <>
      <nav className="flex-1 overflow-y-auto py-3">
        <ul className="space-y-1 px-2">
          {ADMIN_NAV.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-brand-violet/10 text-brand-violet'
                      : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900',
                  )
                }
              >
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <div className="border-t border-slate-200 p-3">
        <button
          type="button"
          onClick={onLogout}
          className="w-full rounded-md px-3 py-2 text-left text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        >
          Cerrar sesión admin
        </button>
      </div>
    </>
  );
}

function PlatformBadge(): JSX.Element {
  return (
    <span className="ml-auto rounded-full bg-brand-violet/10 px-2 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wider text-brand-violet">
      Platform
    </span>
  );
}

/** Wraps all authenticated admin pages. Redirects to /admin/login if not authenticated. */
export function AdminLayout(): JSX.Element {
  const { isAdminAuthenticated, adminLogout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  if (!isAdminAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  const handleLogout = (): void => {
    adminLogout();
    void navigate('/admin/login', { replace: true });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-paper">
      <aside className="hidden h-full w-60 flex-none border-r border-slate-200 bg-white md:flex md:flex-col">
        <div className="flex h-14 items-center gap-2 border-b border-slate-200 px-4">
          <Logo size={24} />
          <PlatformBadge />
        </div>
        <AdminNav onLogout={handleLogout} />
      </aside>

      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} label="Menú de administración">
        <div className="flex h-14 items-center gap-2 border-b border-slate-200 px-4">
          <Logo size={24} />
          <PlatformBadge />
        </div>
        <AdminNav onLogout={handleLogout} />
      </MobileDrawer>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile-only top bar — the admin shell has no desktop header. */}
        <header className="flex h-14 flex-none items-center gap-2 border-b border-slate-200 bg-white px-3 md:hidden">
          <button
            type="button"
            className="rounded-md p-2 text-slate-600 hover:bg-slate-100"
            onClick={() => setDrawerOpen(true)}
            aria-label="Abrir menú"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
              <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
            </svg>
          </button>
          <Logo size={22} />
          <PlatformBadge />
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-6">
            <Outlet />
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
}
